import { getBackendHttpBase } from "@/lib/backendBase";
import type { AnalysisResult } from "@/components/SessionAnalysis";
import type { TranscriptEntry } from "@/lib/geminiSession";

export interface SessionSummary {
  id: string;
  agent_id: string;
  mr_name: string;
  hcp_name?: string | null;
  started_at?: number | null;
  ended_at?: number | null;
  duration_seconds?: number | null;
  overall_score?: number | null;
  created_at?: number | null;
}

export interface SessionDetail extends SessionSummary {
  transcript: TranscriptEntry[];
  report: AnalysisResult;
}

export interface SaveSessionPayload {
  agent_id: string;
  mr_name: string;
  transcript: TranscriptEntry[];
  report: AnalysisResult;
  hcp_name?: string;
  started_at?: number;
  duration_seconds?: number;
}

export async function saveTrainingSession(
  payload: SaveSessionPayload,
): Promise<SessionSummary> {
  const resp = await fetch(`${getBackendHttpBase()}/sessions`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = (await resp.json()) as SessionSummary & { error?: string };
  if (!resp.ok || data.error) {
    throw new Error(data.error ?? `Shranjevanje seje ni uspelo (${resp.status})`);
  }
  return data;
}

export async function listTrainingSessions(params?: {
  mr_name?: string;
  agent_id?: string;
  limit?: number;
  offset?: number;
}): Promise<SessionSummary[]> {
  const qs = new URLSearchParams();
  if (params?.mr_name) qs.set("mr_name", params.mr_name);
  if (params?.agent_id) qs.set("agent_id", params.agent_id);
  if (params?.limit != null) qs.set("limit", String(params.limit));
  if (params?.offset != null) qs.set("offset", String(params.offset));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  const resp = await fetch(`${getBackendHttpBase()}/sessions${suffix}`);
  const data = (await resp.json()) as { sessions?: SessionSummary[]; error?: string };
  if (!resp.ok || data.error) {
    throw new Error(data.error ?? `Nalaganje sej ni uspelo (${resp.status})`);
  }
  return data.sessions ?? [];
}

export async function listMrNames(agentId?: string): Promise<string[]> {
  const qs = agentId ? `?agent_id=${encodeURIComponent(agentId)}` : "";
  const resp = await fetch(`${getBackendHttpBase()}/sessions/mr-names${qs}`);
  const data = (await resp.json()) as { mr_names?: string[]; error?: string };
  if (!resp.ok || data.error) {
    throw new Error(data.error ?? `Nalaganje MR seznama ni uspelo (${resp.status})`);
  }
  return data.mr_names ?? [];
}

export async function getTrainingSession(
  sessionId: string,
): Promise<SessionDetail> {
  const resp = await fetch(`${getBackendHttpBase()}/sessions/${sessionId}`);
  const data = (await resp.json()) as SessionDetail & { error?: string };
  if (!resp.ok || data.error) {
    throw new Error(data.error ?? `Seja ni bila najdena (${resp.status})`);
  }
  return data;
}
